import axios from 'axios';
import { setupAxiosInterceptor } from './auth/axiosConfig';
import config from './config';

// Agent Channels API client (CEL-748/750 §D4) — mirrors the aiEmployeeApi axios + interceptor
// pattern. All endpoints are authenticated; access is org-public within the viewer's scope.
const agentChannelApi = axios.create({
  baseURL: config.API_BASE_URL + '/cellcog',
});

setupAxiosInterceptor(agentChannelApi);

// Channels across the viewer's scopes + joined/unread per channel — default LIVE only.
// { include_archived: 1 } adds archived channels (archive/delete PRD F1 — the sidebar toggle).
// First touch lazily provisions #general server-side (design D6).
export const listChannels = (params = {}) => agentChannelApi.get('/channels', { params });

// Create a channel in the requester's primary scope. 409 on a duplicate (normalized) name.
export const createChannel = (data) => agentChannelApi.post('/channels', data);

// Paginated messages, newest-first pages (callers reverse for display).
// params: { thread_root, before (ISO timestamp), limit }
export const getChannelMessages = (channelId, params = {}) =>
  agentChannelApi.get(`/channels/${channelId}/messages`, { params });

// Nov 2 2026 (translation everywhere): these messages (at most 100) in the viewer's "Languages I read": stored once on the
// server, translated lazily. Returns {active, reader_language, reader_language_name, reader_languages, messages: {id:
// payload}}; active false = nothing in this chat is translated for anyone (stop asking). Support DMs use supportDeskApi.
export const getChannelTranslations = (channelId, ids) =>
  agentChannelApi.get(`/channels/${channelId}/translations`, { params: { ids: (ids || []).slice(0, 100).join(',') } });

// Human post — { markdown, thread_root_id?, mentions_employee_ids?, mentions_user_ids? }.
// Staged human_files/ SHOW_FILE refs are relocated into the channel namespace server-side.
export const postChannelMessage = (channelId, data) =>
  agentChannelApi.post(`/channels/${channelId}/messages`, data);

// CEL-1347: toggle the viewer's (emoji, message) reaction — returns {reacted, reactions,
// reaction_names}. QUIET tier: no unread/notification movement, chips refresh via CHANNEL_CHANGED.
export const toggleReaction = (channelId, messageId, emoji) =>
  agentChannelApi.post(`/channels/${channelId}/messages/${messageId}/reactions`, { emoji });

// CEL-2199: edit the viewer's OWN message — { markdown } (staged human_files/ refs relocate
// server-side like a post). Returns {message, file_urls, mention_names}. Author-only (400 otherwise).
export const editChannelMessage = (channelId, messageId, markdown) =>
  agentChannelApi.post(`/channels/${channelId}/messages/${messageId}/edit`, { markdown });

// CEL-2199: delete a message the viewer wrote, or one written by an employee the viewer OWNS.
// Returns {deleted: 'hard'|'tombstone', message (tombstone row or null), collapsed_root_id, root_id}.
export const deleteChannelMessage = (channelId, messageId) =>
  agentChannelApi.post(`/channels/${channelId}/messages/${messageId}/delete`);

// CEL-1693 (EC Chats app): the EMPLOYEE's view — channels/DMs the employee is a member of,
// each with the employee's own unread/read-cursor/mention state. Read-only; employeeId is the
// employee_post_office_id (what every EC app receives).
export const listEmployeeChannelsView = (employeeId) =>
  agentChannelApi.get(`/channels/employee-view/${employeeId}`);

// Join = create the membership row (prominence + read cursor); idempotent.
export const joinChannel = (channelId) => agentChannelApi.post(`/channels/${channelId}/join`);

// Advance the viewer's read cursor to now.
export const markChannelRead = (channelId) => agentChannelApi.post(`/channels/${channelId}/read`);

// CEL-795 D13: advance the viewer's PER-THREAD read cursor (thread panel open / new visible replies).
export const markThreadRead = (channelId, rootId) =>
  agentChannelApi.post(`/channels/${channelId}/threads/${rootId}/read`);

// CEL-1041: open (find-or-create) a DM for a participant set — refs are "kind:id" pairs
// ("human:<uid>" / "employee:<eid>"); self is auto-included server-side. Returns the DM
// channel row (name = live participant labels) + created flag.
export const openDm = (participants) => agentChannelApi.post('/channels/dm', { participants });

// CEL-1041 D4: convert a DM into a regular channel (one-way; history becomes org-visible).
// Server gates: participants only; human-touched DMs → human participants only.
export const convertDmToChannel = (channelId, name, purpose = null) =>
  agentChannelApi.post(`/channels/${channelId}/convert`, { name, purpose });


// CEL-792: member management (membership = who gets notified; org-public access unchanged).
export const getChannelMembers = (channelId) => agentChannelApi.get(`/channels/${channelId}/members`);
export const addChannelMember = (channelId, kind, id) =>
  agentChannelApi.post(`/channels/${channelId}/members`, { kind, id });
export const removeChannelMember = (channelId, kind, id) =>
  agentChannelApi.post(`/channels/${channelId}/members/remove`, { kind, id });

export const archiveChannel = (channelId, archived = true) =>
  agentChannelApi.post(`/channels/${channelId}/archive`, { archived });

// Channel management v1 (agent_docs/channel_management_v1.md). One channel's management state:
// { is_manual, viewer_is_manager, managers: [{kind, id, name, is_creator, owner_name}],
//   mirror: null | { kind: 'workstream'|'team', id, name, slug?, purpose?, viewer_can_change } }.
export const getChannelManagement = (channelId) =>
  agentChannelApi.get(`/channels/${channelId}/management`);
// Manual channels, managers only: rename in place (same id + history; a quiet line records it).
// A live workstream/team mirror answers 409 — rename the workstream/team instead.
export const renameChannel = (channelId, name) =>
  agentChannelApi.post(`/channels/${channelId}/rename`, { name });
// Description (purpose line); empty clears it. On a workstream channel it edits the workstream's purpose.
export const setChannelPurpose = (channelId, purpose) =>
  agentChannelApi.post(`/channels/${channelId}/purpose`, { purpose });
export const addChannelManager = (channelId, kind, id) =>
  agentChannelApi.post(`/channels/${channelId}/managers`, { kind, id });
export const removeChannelManager = (channelId, kind, id) =>
  agentChannelApi.post(`/channels/${channelId}/managers/remove`, { kind, id });
// "Rename workstream" from its work-<slug> channel (the workstream cascade: channel + task tags).
// Team channels rename through the existing team route (organizationApi.patchTeam).
export const renameChannelWorkstream = (channelId, name) =>
  agentChannelApi.post(`/channels/${channelId}/workstream/rename`, { name });

// Archive/delete PRD F2: HARD delete (org-admin humans only; #general 409s). Permanent —
// messages, memberships, subscriptions, and channel-namespace files all cascade server-side.
export const deleteChannel = (channelId) => agentChannelApi.post(`/channels/${channelId}/delete`);
// Close conversation (DMs only): hides the DM from the caller's sidebar until someone writes in it.
export const closeDm = (channelId) => agentChannelApi.post(`/channels/${channelId}/close`);
